import { SIGNAL_STORM_CAPTURE_SPEED_MULT } from '../../data/signalStorm.js';
import {
  SIGNAL_SURGE_CAPTURE_SPEED_MULT,
  isSignalSurgeActive,
} from '../../data/signalSurge.js';
import type { MatchState } from '../State.js';
import { SECONDS_PER_TICK } from '../State.js';
import type { ControlNode } from '../entities/ControlNode.js';
import type { PlayerId } from '../ids.js';

const CAPTURE_RADIUS_PX = 120;
const CAPTURE_RADIUS_SQ = CAPTURE_RADIUS_PX * CAPTURE_RADIUS_PX;
// Per docs/02_GAME_DESIGN.md §4: ~10 s neutral, ~14 s flipping. Splitting that into two
// rates means tearing down enemy control (sign opposes capturer) is faster than
// establishing your own, so a full -1 → +1 flip = 4 s teardown + 10 s establish ≈ 14 s.
const ESTABLISH_RATE_PER_SEC = 1 / 10;
const TEARDOWN_RATE_PER_SEC = 1 / 4;
// Held nodes generate +3 S/sec per docs/02_GAME_DESIGN.md §4.
const HELD_NODE_INCOME_PER_SEC = 3;
// V1: Engineer captures relays 50% faster (docs/02_GAME_DESIGN.md §2 + §4.4).
// Applied only when the dominant capturing squad in radius is an Engineer; mixed
// rosters fall back to the base rate so a Recon-led capture isn't speed-up by a
// trailing Engineer.
const ENGINEER_CAPTURE_MULT = 1.5;

// Sign convention: +1 means player 0 fully holds, -1 means player 1 fully holds. The
// "direction" of a capturing player is +1 for player 0, -1 for player 1.
function directionFor(playerId: PlayerId): 1 | -1 {
  return playerId === 0 ? 1 : -1;
}

export function captureSystem(state: MatchState): void {
  for (const node of Object.values(state.controlNodes)) {
    let p0Count = 0;
    let p1Count = 0;
    let p0HasEngineer = false;
    let p1HasEngineer = false;
    let p0HasNonEngineer = false;
    let p1HasNonEngineer = false;
    for (const squad of Object.values(state.squads)) {
      const dx = squad.pos.x - node.pos.x;
      const dy = squad.pos.y - node.pos.y;
      if (dx * dx + dy * dy > CAPTURE_RADIUS_SQ) continue;
      if (squad.ownerId === 0) {
        p0Count += 1;
        if (squad.archetype === 'engineer') p0HasEngineer = true;
        else p0HasNonEngineer = true;
      } else {
        p1Count += 1;
        if (squad.archetype === 'engineer') p1HasEngineer = true;
        else p1HasNonEngineer = true;
      }
    }

    if (p0Count > 0 && p1Count > 0) {
      node.contested = true;
      continue;
    }
    node.contested = false;

    if (p0Count === 0 && p1Count === 0) continue;

    const capturer: PlayerId = p0Count > 0 ? 0 : 1;
    const engineerLed = capturer === 0
      ? engineerDominates(p0HasEngineer, p0HasNonEngineer)
      : engineerDominates(p1HasEngineer, p1HasNonEngineer);
    advanceCapture(state, node, capturer, engineerLed);
  }
}

// "Dominant" means the Engineer is the only archetype contributing — a single
// non-Engineer in the radius reverts to base capture rate. Keeps the bonus
// strategically distinct: send a pure Engineer to the relay for tempo.
function engineerDominates(hasEngineer: boolean, hasNonEngineer: boolean): boolean {
  return hasEngineer && !hasNonEngineer;
}

function advanceCapture(
  state: MatchState,
  node: ControlNode,
  capturer: PlayerId,
  engineerLed: boolean,
): void {
  const dir = directionFor(capturer);
  // If the capturer already fully holds the node, there's nothing to do.
  if (node.ownerId === capturer && node.captureProgress * dir >= 1) return;
  // Tearing down enemy progress (current sign opposes us) is faster than establishing
  // our own. Crossing zero swaps which rate applies.
  const isTearingDown = node.captureProgress * dir < 0;
  const baseRate = isTearingDown ? TEARDOWN_RATE_PER_SEC : ESTABLISH_RATE_PER_SEC;
  let ratePerSec = engineerLed ? baseRate * ENGINEER_CAPTURE_MULT : baseRate;
  // V1.5+ Slice 8 — Signal Storm Endgame: contested relays flip ~1.5× faster
  // during the 7:00–8:00 storm window. Applies to BOTH sides equally so it's
  // not asymmetric pressure. Composes multiplicatively with the Engineer
  // bonus: an Engineer in storm captures at 1.5 × 1.5 = 2.25× — a clear
  // late-game payoff for the player who built and held an Engineer.
  if (state.phase === 'signal_storm') ratePerSec *= SIGNAL_STORM_CAPTURE_SPEED_MULT;
  // V1.5+ Signal Surge — a short mid-match capture-speed window at 5:30.
  // Symmetric (both sides get it); never overlaps the storm window so the
  // two multipliers don't double-stack. See data/signalSurge.ts.
  if (isSignalSurgeActive(state.matchSeconds)) ratePerSec *= SIGNAL_SURGE_CAPTURE_SPEED_MULT;
  const delta = ratePerSec * SECONDS_PER_TICK * dir;
  let next = node.captureProgress + delta;
  // Don't overshoot the boundary in a single tick; if we cross zero, clamp at zero so
  // the next tick recomputes which rate applies.
  if (isTearingDown && Math.sign(next) !== Math.sign(node.captureProgress) && node.captureProgress !== 0) {
    next = 0;
  }
  if (next > 1) next = 1;
  if (next < -1) next = -1;
  node.captureProgress = next;

  const prevOwnerId = node.ownerId;
  if (next >= 1) {
    if (node.ownerId !== 0) {
      node.ownerId = 0;
      node.generatesSignal = HELD_NODE_INCOME_PER_SEC;
    }
  } else if (next <= -1) {
    if (node.ownerId !== 1) {
      node.ownerId = 1;
      node.generatesSignal = HELD_NODE_INCOME_PER_SEC;
    }
  }
  // While progress is in (-1, +1), ownership and income are sticky to whoever last
  // reached an extreme. This makes "14 s flipping enemy" meaningful: the original owner
  // keeps the relay (and the income) until the attacker fully completes the flip.

  // V1.5+ telemetry pre-flight: count a flip when ownership becomes non-null
  // (a fresh capture). Neutralisation (ownerId → null) is counted as an
  // implicit pre-step of a future flip, not as its own event — what the §17
  // gate cares about is "how often does an active capture land", not "how
  // often does someone leave a relay alone".
  if (node.ownerId !== null && node.ownerId !== prevOwnerId) {
    state.stats.relayFlipsByPlayer[node.ownerId] += 1;
    // V1.5+ Slice 8 — Signal Storm Endgame: track flips that landed
    // during the storm window separately. The §17 final-minute
    // requirement is closed by this metric — the storm must produce
    // meaningful flips in the 60 s window, not just shift them around.
    // Subset of relayFlipsByPlayer (every storm flip is also a regular
    // flip), so no double-counting at the playtest aggregator.
    if (state.phase === 'signal_storm') {
      state.stats.signalStormFlipsByPlayer[node.ownerId] += 1;
    }
  }
}

// Exported for tests so they can hand-build a node + squad and assert the Engineer
// rate matches design (50% faster than base). Keeps the constant private otherwise.
export const ENGINEER_CAPTURE_MULTIPLIER = ENGINEER_CAPTURE_MULT;
